import type {
  TranscriptWord,
  VideoChapter,
  VideoEditorMaterial,
  VideoSegment,
  VideoSummary,
} from '@live-stream/shared'
import type { Videos } from '../api/videos.js'
import { derived, patch, readonly, writable, type ReadableStore } from '../store.js'
import { addCut, inCut, keepFromRemoved, rangeOfWords, removedFromKeep, skipTo } from './cuts.js'

/**
 * The cut editor's draft (Phase 9): what is cut and which chapters are
 * pinned, on the original's clock, until the owner applies it. Nothing
 * touches the file before `apply()` (or `copy()`, which leaves this video
 * as it is). A page owns one; it hands over the video it shows and takes
 * back the one the server returns.
 */

export interface CutEditorState {
  open: boolean
  /** The original's words, pauses and frames; loaded on open. */
  material: VideoEditorMaterial | null
  /** The stretches cut out, sorted and merged. */
  removed: VideoSegment[]
  /** The chapters as being edited. */
  chapters: VideoChapter[]
  saving: boolean
}

/** What the editor needs from the page that owns it. */
export interface CutEditorHost {
  readonly video: ReadableStore<VideoSummary | null>
  adopt(video: VideoSummary): void
  fail(err: unknown, fallback: string): void
}

const sameSegments = (a: readonly VideoSegment[], b: readonly VideoSegment[]): boolean =>
  a.length === b.length && a.every((x, i) => x.s === b[i]!.s && x.e === b[i]!.e)

const sameChapters = (a: readonly VideoChapter[], b: readonly VideoChapter[]): boolean =>
  a.length === b.length && a.every((c, i) => c.atMs === b[i]!.atMs && c.title === b[i]!.title)

const initialState = (): CutEditorState => ({
  open: false,
  material: null,
  removed: [],
  chapters: [],
  saving: false,
})

export class CutEditor {
  private readonly store = writable<CutEditorState>(initialState())
  readonly state: ReadableStore<CutEditorState> = readonly(this.store)
  /** The original's length: the editor's clock. */
  readonly sourceDuration: ReadableStore<number>
  readonly cutsDirty: ReadableStore<boolean>
  readonly chaptersDirty: ReadableStore<boolean>
  /** Anything to apply. */
  readonly dirty: ReadableStore<boolean>

  constructor(
    private readonly videos: Videos,
    private readonly host: CutEditorHost,
  ) {
    this.sourceDuration = derived(
      [this.store, host.video],
      (e, v) => e.material?.sourceDurationMs ?? v?.sourceDurationMs ?? 0,
    )
    this.cutsDirty = derived(
      [this.store, this.sourceDuration],
      (e, d) =>
        e.material !== null && !sameSegments(keepFromRemoved(e.removed, d), e.material.keep),
    )
    this.chaptersDirty = derived(
      [this.store],
      (e) => e.material !== null && !sameChapters(e.chapters, e.material.chapters),
    )
    this.dirty = derived([this.cutsDirty, this.chaptersDirty], (c, ch) => c || ch)
  }

  /** Load the original and start from what is applied now; false when it could not open. */
  async open(): Promise<boolean> {
    const video = this.host.video.get()
    if (!video) return false
    try {
      const material = await this.videos.editor(video.id)
      patch(this.store, {
        material,
        removed: removedFromKeep(material.keep, material.sourceDurationMs ?? 0),
        chapters: material.chapters.map((c) => ({ ...c })),
        open: true,
      })
      return true
    } catch (err) {
      this.host.fail(err, 'Could not open the editor.')
      return false
    }
  }

  close(): void {
    patch(this.store, { open: false })
  }

  /** Closed and empty: another video. */
  reset(): void {
    this.store.set(initialState())
  }

  setRemoved(removed: VideoSegment[]): void {
    patch(this.store, { removed })
  }

  setChapters(chapters: VideoChapter[]): void {
    patch(this.store, { chapters })
  }

  /** Take a stretch out. */
  cut(range: VideoSegment): void {
    patch(this.store, {
      removed: addCut(this.store.get().removed, range, this.sourceDuration.get()),
    })
  }

  /** The stretch words `from`..`to` of the original cover, padded; null without material. */
  rangeOfWords(from: number, to: number): VideoSegment | null {
    const material = this.store.get().material
    return material ? rangeOfWords(material.words, from, to) : null
  }

  /** A word that falls inside a cut. */
  isCut(word: Pick<TranscriptWord, 's'>): boolean {
    return inCut(this.store.get().removed, word.s)
  }

  /** Previewing: a moment inside a cut jumps to its end; null to play on. */
  skip(ms: number): number | null {
    const ahead = skipTo(ms, this.store.get().removed)
    return ahead !== null && ahead < this.sourceDuration.get() ? ahead : null
  }

  private async saveChapters(videoId: string): Promise<void> {
    if (!this.chaptersDirty.get()) return
    const chapters = this.store.get().chapters.filter((c) => c.title.trim())
    this.host.adopt(await this.videos.update(videoId, { chapters }))
  }

  /** Save the chapters and render the cuts; true once done, and the editor closes. */
  async apply(): Promise<boolean> {
    const video = this.host.video.get()
    if (!video || !this.store.get().material) return false
    return this.saving('Could not apply the cuts.', async () => {
      await this.saveChapters(video.id)
      if (this.cutsDirty.get()) {
        const keep = keepFromRemoved(this.store.get().removed, this.sourceDuration.get())
        this.host.adopt(await this.videos.saveCuts(video.id, keep))
      }
      this.close()
    })
  }

  /** The cut version as a video of its own; its share token, or null when it failed. */
  async copy(): Promise<string | null> {
    const video = this.host.video.get()
    if (!video || !this.store.get().material) return null
    let token: string | null = null
    await this.saving('Could not save the new video.', async () => {
      await this.saveChapters(video.id)
      const keep = keepFromRemoved(this.store.get().removed, this.sourceDuration.get())
      token = (await this.videos.copyWithCuts(video.id, keep)).shareToken
      this.close()
    })
    return token
  }

  /** Back to the original, uncut. */
  async revert(): Promise<boolean> {
    const video = this.host.video.get()
    if (!video) return false
    return this.saving('Could not restore the video.', async () => {
      this.host.adopt(await this.videos.clearCuts(video.id))
      this.close()
    })
  }

  private async saving(fallback: string, run: () => Promise<void>): Promise<boolean> {
    patch(this.store, { saving: true })
    try {
      await run()
      return true
    } catch (err) {
      this.host.fail(err, fallback)
      return false
    } finally {
      patch(this.store, { saving: false })
    }
  }
}
